import { type PlacementWithLogical, Text } from '@chakra-ui/react';
import classNames from 'classnames';
import {
  type ReactNode,
  type RefObject,
  useEffect,
  useRef,
  useState,
} from 'react';
import type { SingleValue } from 'react-select';
import { useCookie } from 'react-use';

import { useUserTrialDetails } from '@revelio/auth';
import { FeatureFlag } from '@revelio/core';
import { Popover, type PopoverProps, useDisclosure } from '@revelio/ui';

import { isEventInsideEntityInfoPopover } from '../../../../shared/components/tree/EntityInfoPopover';
import { useUnleashFlag } from '../../../../shared/unleash-flags/use-unleash-flag';
import { UserPreferenceControlledFilterIds } from '../../../../shared/user-preferences/types';
import { useEditMode } from '../../components/EditModeProvider/EditModeProvider';
import {
  usePageConfig,
  useTabContentConfig,
} from '../../components/PageConfigProvider/PageConfigProvider';
import type { SecondaryFilterConfig } from '../../components/PageConfigProvider/page-reducer';
import { useFilterContext } from '../contexts/FilterProvider';
import {
  ALL_FILTER_DEFINITIONS,
  getFilterDefinition,
} from '../engine/filterRegistry';
import { useFilters } from '../hooks/useFilters';
import { useMultiFilterEditor } from '../hooks/useMultiFilterEditor';
import {
  type Filter,
  FilterId,
  type FilterSelectOptions,
  FilterType,
} from '../types/filters';
import { isTreeFilter } from '../utils/common';
import { canFilterBeSegmented } from '../utils/filterSegmentation';
import styles from './FilterPopover.module.css';
import { FilterSelectDropdown } from './FilterSelectDropdown';
import { FilterSelectList } from './FilterSelectList';
import {
  DEFAULT_AVAILABLE_FILTER_IDS,
  FilterSelectModal,
} from './FilterSelectModal';
import { onFilterMenuOpenRequest } from './filter-menu-open-signal';
import layoutStyles from './filter-popover.module.css';
import { FilterFormRender } from './filterForms/FilterFormRender';
import {
  getGroupFilterOptions,
  getSubFilterLabelPostfix,
  SubFilter,
} from './SubFilter';

interface FilterPopoverProps {
  trigger: ReactNode;
  filter?: Filter;
  placement?: PlacementWithLogical;
}

type PopoverPlacement = NonNullable<PopoverProps['placement']>;

const POPOVER_PLACEMENTS: readonly PopoverPlacement[] = [
  'top',
  'top-start',
  'top-end',
  'bottom',
  'bottom-start',
  'bottom-end',
  'left',
  'left-start',
  'left-end',
  'right',
  'right-start',
  'right-end',
];

// Callers still pass Chakra's placement vocabulary; its `auto*` and logical
// (`start`/`end`) values have no Radix counterpart and fall back to the default.
const isPopoverPlacement = (
  placement: PlacementWithLogical
): placement is PopoverPlacement =>
  (POPOVER_PLACEMENTS as readonly string[]).includes(placement);

/**
 * Children that render outside the surface's DOM subtree: every react-select
 * in these forms carries a `classNamePrefix`, so its `document.body` portal,
 * menu and options all wear a `<prefix>__menu*` class; Chakra's `<Portal>`
 * hosts the company tree's entity-info tooltip and the toasts.
 */
const PORTALED_CHILD_SELECTOR = '[class*="__menu"], .chakra-portal';

/**
 * Radix dismisses on pointer-down outside the surface, where Chakra dismissed
 * on blur — and Chakra only survived react-select because the menu
 * preventDefaults its mousedown so focus never leaves. A body-portaled menu is
 * "outside" by Radix's measure even though the user is still working in the
 * filter, so without this, picking an option would close the whole popover.
 */
export const keepOpenForPortaledChildren: NonNullable<
  PopoverProps['onInteractOutside']
> = (event) => {
  const target = event.target as HTMLElement | null;
  if (
    target?.closest(PORTALED_CHILD_SELECTOR) ||
    isEventInsideEntityInfoPopover(event)
  ) {
    event.preventDefault();
  }
};

/**
 * Chakra's `initialFocusRef`: focus the form's search input as the surface
 * opens. When none is mounted (a bound filter's form has no search box) Radix's
 * default — the first tabbable element — stands.
 */
export const focusOnOpen = (ref: RefObject<HTMLElement>) => (event: Event) => {
  const element = ref.current;
  if (!element) return;
  event.preventDefault();
  element.focus();
};

const getRegularFilterOptions = (
  allOptions: FilterSelectOptions[],
  primaryFilterId?: FilterId
): FilterSelectOptions[] => {
  const result: FilterSelectOptions[] = [];

  allOptions.forEach((option) => {
    const filterId: FilterId = option.id;
    const filterDef = ALL_FILTER_DEFINITIONS[filterId];

    // If it's a GROUP filter, expand it to show its children
    if (filterDef?.type === FilterType.GROUP) {
      const childOptions = getGroupFilterOptions(filterId, primaryFilterId);
      result.push(...childOptions);
    } else {
      result.push(option);
    }
  });

  return result;
};

const shouldSelectFilter = (filter: FilterSelectOptions): boolean => {
  const filterId: FilterId = filter.id;
  const filterDef = ALL_FILTER_DEFINITIONS[filterId];
  return !filterDef?.children || filterDef.children.length === 0;
};

export const FilterPopover = ({
  trigger,
  filter,
  placement = 'bottom-start',
}: FilterPopoverProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const { filters } = useFilters();

  const {
    config,
    revelioPageId: revelioPageIdFromTab,
    primaryFilter,
    dispatch,
  } = useTabContentConfig();
  const { config: pageConfig } = usePageConfig();
  const { editMode } = useEditMode();
  const revelioPageId = revelioPageIdFromTab ?? pageConfig?.revelioPageId;
  const { secondaryFilters } = config;
  const isCustomPage = !revelioPageId;
  const isValuationEnabled = useUnleashFlag(
    FeatureFlag.CompanyDiscoveryValuationFilter
  );
  const { activityTaxonomyEnabled: isActivityTaxonomyEnabled } =
    useFilterContext();

  const {
    selectedFilter,
    selectedFilterId,
    selectedMultiMeta,
    selectFilter,
    clearSelection,
  } = useMultiFilterEditor({
    filters,
    initialFilter: filter,
    isOpen,
  });

  const showSubFilter = selectedFilter && canFilterBeSegmented(selectedFilter);

  const editFiltersModalDisclosure = useDisclosure();

  // Get available filter IDs based on page configuration
  const availableFilterIds: FilterId[] = (() => {
    // If page has explicit secondaryFilters configured, use those
    if (secondaryFilters.length > 0) {
      return secondaryFilters.map((f) => f.id);
    }

    // For custom pages with no secondaryFilters, use the whitelist
    if (isCustomPage) {
      return Array.from(filters.keys()).filter((f) =>
        DEFAULT_AVAILABLE_FILTER_IDS.includes(f)
      );
    }

    // For core Revelio pages with no secondaryFilters, show all filters (existing behavior)
    return Array.from(filters.keys()).filter((f) => {
      return ![
        FilterId.MULTI_TITLE,
        FilterId.MULTI_KEYWORDS,
        FilterId.MULTI_CUSTOM_SKILL,
        FilterId.MULTI_ACTIVITY,
      ].includes(f);
    });
  })();

  const filterOptions: FilterSelectOptions[] = [
    ...availableFilterIds
      .filter(
        (filterId) => !UserPreferenceControlledFilterIds.includes(filterId)
      )
      .filter((filterId) => {
        // Always exclude the legacy SKILL filter since it gets migrated to CUSTOM_SKILL
        if (filterId === FilterId.SKILL) {
          return false;
        }
        if (
          (filterId === FilterId.ACTIVITY ||
            filterId === FilterId.MULTI_ACTIVITY) &&
          !isActivityTaxonomyEnabled
        ) {
          return false;
        }
        return true;
      })
      .map<FilterSelectOptions>((filterId) => {
        const filterDefinition = getFilterDefinition(filterId);
        return {
          id: filterId,
          name: filterDefinition?.name ?? String(filterId),
          labelPostfix: getSubFilterLabelPostfix(filterId, primaryFilter?.id),
        };
      })
      .sort((a, b) => {
        const an = a?.name ?? '';
        const bn = b?.name ?? '';
        return an.localeCompare(bn);
      }),
  ];

  // Filter options for the main menu (excluding only child filters of GROUP filters)
  const mainMenuFilterOptions = filterOptions.filter((option) => {
    const filterId: FilterId = option.id;
    const isChildFilter = Object.values(ALL_FILTER_DEFINITIONS).some(
      (def) => def.type === FilterType.GROUP && def.children?.includes(filterId)
    );

    return !isChildFilter;
  });

  const handleOpenFilterMenu = () => {
    if (!filter) {
      clearSelection();
    }
    setIsOpen(true);
  };

  // Only the main filter menu (no bound filter) answers open requests from
  // elsewhere on the page, e.g. the screener empty-state CTA.
  useEffect(() => {
    if (filter) {
      return;
    }
    return onFilterMenuOpenRequest(() => {
      clearSelection();
      setIsOpen(true);
    });
  }, [filter, clearSelection]);

  const handleClose = () => {
    setIsOpen(false);
    setTimeout(() => {
      clearSelection();
    }, 100);
  };

  const handleFilterChange = (
    selected: SingleValue<{
      label: string;
      value: FilterSelectOptions['id'];
    }>
  ) => {
    if (selected) {
      selectFilter(selected.value);
    } else {
      clearSelection();
    }
  };

  const [filterAttention, updateFilterAttention] =
    useCookie('filter-attention');

  const { isSelfSignupUser } = useUserTrialDetails<boolean>({
    initialRenderValue: false,
  });

  useEffect(() => {
    if (!filterAttention && isOpen && !filter) {
      updateFilterAttention('false');
    }
  }, [filterAttention, updateFilterAttention, isOpen, filter]);

  const shouldShowAttentionIndicator =
    isSelfSignupUser && !filterAttention && !filter;

  const selectedFilterDef = selectedFilterId
    ? ALL_FILTER_DEFINITIONS[selectedFilterId]
    : null;

  const dropdownOptions = (() => {
    let options: FilterSelectOptions[];
    if (!selectedFilterDef || !selectedFilterId) {
      options = filterOptions;
    } else if (selectedFilterDef.type === FilterType.GROUP) {
      options = getGroupFilterOptions(selectedFilterId, primaryFilter?.id);
    } else {
      options = getRegularFilterOptions(filterOptions, primaryFilter?.id);
    }

    if (!isValuationEnabled) {
      return options.filter((o) => o.id !== FilterId.VALUATION);
    }
    return options;
  })();

  const popoverContent = (() => {
    if (!selectedFilter) {
      return (
        <FilterSelectList
          filterOptions={mainMenuFilterOptions}
          setSelectedFilter={(filter) => {
            if (shouldSelectFilter(filter)) {
              selectFilter(filter.id);
            }
          }}
          closeMenu={handleClose}
          editFiltersModalDisclosure={editFiltersModalDisclosure}
          searchInputRef={searchInputRef}
        />
      );
    }
    if (showSubFilter) {
      return (
        <SubFilter
          filterSelectDropdown={
            !filter ? (
              <FilterSelectDropdown
                filterOptions={dropdownOptions}
                selectedFilter={selectedFilterId}
                onChangeSelectedFilter={handleFilterChange}
              />
            ) : (
              <Text fontSize="sm" className={styles.mutedName}>
                {selectedFilter?.name}
              </Text>
            )
          }
          filter={selectedFilter}
          close={handleClose}
          selectedMultiMeta={selectedMultiMeta}
        />
      );
    }
    return (
      <div data-testid="filter-menu-popover">
        {!filter && (
          <div className={styles.popoverHeader}>
            <FilterSelectDropdown
              filterOptions={dropdownOptions}
              selectedFilter={selectedFilterId}
              onChangeSelectedFilter={handleFilterChange}
            />
          </div>
        )}
        <FilterFormRender
          key={selectedFilter?.id ?? 'none'}
          filter={selectedFilter}
          close={handleClose}
          selectedMultiMeta={selectedMultiMeta}
          showSelectedValues={false}
          currentTreeSelections={
            selectedFilter && isTreeFilter(selectedFilter)
              ? selectedFilter.selections
              : undefined
          }
        />
      </div>
    );
  })();

  return (
    <>
      <Popover
        placement={isPopoverPlacement(placement) ? placement : 'bottom-start'}
        open={isOpen && !editFiltersModalDisclosure.isOpen}
        onOpenChange={(next) => (next ? handleOpenFilterMenu() : handleClose())}
        onOpenAutoFocus={focusOnOpen(searchInputRef)}
        onInteractOutside={keepOpenForPortaledChildren}
        className={classNames(styles.popover, {
          [styles.activityPopoverContent ?? '']:
            selectedFilterId === FilterId.ACTIVITY,
        })}
        data-testid="filter-popover-content-open"
        trigger={
          <div className={layoutStyles.triggerWrapper}>
            {trigger}
            {shouldShowAttentionIndicator && (
              <div className={styles.indicatorRing} />
            )}
          </div>
        }
      >
        {popoverContent}
      </Popover>
      {/* Beside the popover, not inside it: the popover closes to make room
          for the modal, and Radix unmounts a closed surface's content. */}
      {editMode && !filter && (
        <FilterSelectModal
          isOpen={editFiltersModalDisclosure.isOpen}
          onClose={editFiltersModalDisclosure.onClose}
          currentFilters={config.secondaryFilters}
          onSave={(filters: SecondaryFilterConfig[]) => {
            dispatch({
              type: 'SET_SECONDARY_FILTERS',
              payload: { filters },
            });
          }}
        />
      )}
    </>
  );
};
